import React, { useEffect, useState } from "react";
import axios from "axios";
import CustomNavbar from "../CustomPermissions/CustomNavbar";

import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../../config/api";
import { exportMonthlyComparisonToExcel } from "../../utils/exportUtils";

const FilterMonthlyRecords = () => {
  const [records, setRecords] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track expanded row with a composite key (userIndex-rowIndex)
  const [expandedKey, setExpandedKey] = useState(null);
  const [expandedRowType, setExpandedRowType] = useState(null);

  const [candidateDetails, setCandidateDetails] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const response = await axios.get(getApiUrl("/Docladder/FilterMonthlyRecords/", false), {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
        setRecords(response.data);
      } catch (err) {
        setError("Failed to fetch records");
      } finally {
        setLoading(false);
      }
    };
    fetchRecords();
  }, []);

  const sortedRecords = Object.entries(records).sort(([a], [b]) => a.localeCompare(b));

  const toggleExpandedRow = async (userIndex, rowIndex, type, monthStart, monthEnd, userId) => {
    const newKey = `${userIndex}-${rowIndex}`;

    if (expandedKey === newKey && expandedRowType === type) {
      // Collapse the row if already expanded
      setExpandedKey(null);
      setExpandedRowType(null);
      setCandidateDetails([]);
    } else {
      // Expand the row and fetch candidate details
      setExpandedKey(newKey);
      setExpandedRowType(type);
      setLoadingCandidates(true);

      try {
        const response = await axios.get(
          getApiUrl(`/Docladder/GetAllCandidateOfUser/${userId}/`, false),
          {
            params: {
              start_date: monthStart,
              end_date: monthEnd,
              status: type,
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        setCandidateDetails(response.data.data || []);
      } catch (error) {
        console.error("Error fetching candidate details:", error);
      } finally {
        setLoadingCandidates(false);
      }
    }
  };

  if (loading) return <div className="text-center mt-5">Loading records...</div>;
  if (error) return <div className="text-center text-red-500 mt-5">{error}</div>;

  return (
    <>
      <div className="flex">
        <CustomNavbar />
        <div style={{ width: "100vw" }}>
          <div className="flex justify-between items-center bg-blue-500 text-white p-4">
            <h1 className="text-2xl font-bold">
              Docladder Month-wise Record
            </h1>
            <button
              onClick={() => exportMonthlyComparisonToExcel(records, `Docladder_Monthly_${new Date().toISOString().split('T')[0]}`)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              📥 Export All to Excel
            </button>
          </div>
          <div style={{ width: "90vw", margin: "auto" }}>
            {sortedRecords.map(([username, userData], userIndex) => {
              // Ensure userData contains an 'id' field
              if (!userData.id) {
                console.error(`Missing 'id' for user: ${username}`);
                return null;
              }

              const userRecords = userData.records || [];
              const totals = userRecords.reduce(
                (acc, record) => ({
                  total_resume_forwarded: acc.total_resume_forwarded + record.total_resume_forwarded,
                  total_pipeline: acc.total_pipeline + (record.total_pipeline || 0),
                  total_pipeline_unique: acc.total_pipeline_unique + (record.total_pipeline_unique || 0),
                  total_turned_up: acc.total_turned_up + record.total_turned_up,
                  total_selected: acc.total_selected + record.total_selected,
                  total_backout: acc.total_backout + (record.total_backout || 0),
                  total_revenue: acc.total_revenue + record.total_revenue,
                }),
                {
                  total_resume_forwarded: 0,
                  total_pipeline: 0,
                  total_pipeline_unique: 0,
                  total_turned_up: 0,
                  total_selected: 0,
                  total_backout: 0,
                  total_revenue: 0,
                }
              );

              return (
                <div key={username} className="bg-white shadow-md rounded-lg p-4 my-3">
                  <h2 className="text-xl font-semibold text-blue-600 mb-2">{userData.name}</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full border border-gray-300 text-center">
                      <thead className="bg-blue-500 text-white">
                        <tr>
                          <th className="border px-4 py-2">Month Start</th>
                          <th className="border px-4 py-2">Month End</th>
                          <th className="border px-4 py-2">Resume Forwarded</th>
                          <th className="border px-4 py-2">Pipeline</th>
                          <th className="border px-4 py-2">Turned Up</th>
                          <th className="border px-4 py-2">Selected</th>
                          <th className="border px-4 py-2">Backout</th>
                          <th className="border px-4 py-2">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userRecords.map((record, rowIndex) => {
                          const rowKey = `${userIndex}-${rowIndex}`;
                          return (
                            <React.Fragment key={rowIndex}>
                              {/* Main Row */}
                              <tr className="hover:bg-gray-100">
                                <td className="border px-4 py-2">{record.month_start}</td>
                                <td className="border px-4 py-2">{record.month_end}</td>
                                <td
                                  className="border px-4 py-2 text-blue-500 cursor-pointer"
                                  onClick={() =>
                                    toggleExpandedRow(
                                      userIndex,
                                      rowIndex,
                                      "resume_forwarded",
                                      record.month_start,
                                      record.month_end,
                                      userData.id
                                    )
                                  }
                                >
                                  {record.total_resume_forwarded}
                                </td>
                                <td
                                  className="border px-4 py-2 text-blue-500 cursor-pointer"
                                  onClick={() =>
                                    toggleExpandedRow(
                                      userIndex,
                                      rowIndex,
                                      "pipeline",
                                      record.month_start,
                                      record.month_end,
                                      userData.id
                                    )
                                  }
                                >
                                  {record.total_pipeline_unique || 0} / {record.total_pipeline || 0}
                                </td>
                                <td className="border px-4 py-2">{record.total_turned_up}</td>
                                <td
                                  className="border px-4 py-2 text-blue-500 cursor-pointer"
                                  onClick={() =>
                                    toggleExpandedRow(
                                      userIndex,
                                      rowIndex,
                                      "selected",
                                      record.month_start,
                                      record.month_end,
                                      userData.id
                                    )
                                  }
                                >
                                  {record.total_selected}
                                </td>
                                <td
                                  className="border px-4 py-2 text-blue-500 cursor-pointer"
                                  onClick={() =>
                                    toggleExpandedRow(
                                      userIndex,
                                      rowIndex,
                                      "backout",
                                      record.month_start,
                                      record.month_end,
                                      userData.id
                                    )
                                  }
                                >
                                  {record.total_backout || 0}
                                </td>
                                <td className="border px-4 py-2">{record.total_revenue.toFixed(2)}</td>
                              </tr>

                              {/* Expanded Row */}
                              {expandedKey === rowKey && (
                                <tr className="bg-gray-50">
                                  <td colSpan="8" className="p-3">
                                    <div className="grid grid-cols-1 gap-4">
                                      <h3 className="text-lg font-semibold text-blue-600 mb-2">
                                        {expandedRowType === "resume_forwarded"
                                          ? "Resume Forwarded Candidates"
                                          : expandedRowType === "pipeline"
                                          ? "Pipeline Candidates"
                                          : expandedRowType === "backout"
                                          ? "Backed Out Candidates"
                                          : "Selected Candidates"}
                                      </h3>
                                      {loadingCandidates ? (
                                        <div className="flex justify-center items-center space-x-2 text-blue-500">
                                          <span className="animate-spin text-3xl">Loading...</span>
                                          <p className="text-xl font-medium">Fetching candidate details...</p>
                                        </div>
                                      ) : (
                                        <table className="min-w-full bg-white border rounded-lg shadow-md">
                                          <thead>
                                            <tr className="bg-blue-500 text-white text-left">
                                              <th className="py-3 px-4">Candidate Name</th>
                                              <th className="py-3 px-4">Hospital</th>
                                              <th className="py-3 px-4">City</th>
                                              <th className="py-3 px-4">State</th>
                                              <th className="py-3 px-4">Speciality</th>
                                              <th className="py-3 px-4">Status</th>
                                              <th className="py-3 px-4">Revenue</th>
                                              <th className="py-3 px-4">Date Entered</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {candidateDetails.length > 0 ? (
                                              candidateDetails.map((candidate) => (
                                                <tr key={candidate.id}>
                                                  <td className="py-3 px-4">{candidate.candidate_name}</td>
                                                  <td className="py-3 px-4">{candidate.hospital}</td>
                                                  <td className="py-3 px-4">{candidate.city}</td>
                                                  <td className="py-3 px-4">{candidate.state}</td>
                                                  <td className="py-3 px-4">{candidate.speciality}</td>
                                                  <td className="py-3 px-4">{candidate.status}</td>
                                                  <td className="py-3 px-4">{candidate.revenue}</td>
                                                  <td className="py-3 px-4">{candidate.date_entered}</td>
                                                </tr>
                                              ))
                                            ) : (
                                              <tr>
                                                <td colSpan="8" className="py-8 text-center text-gray-500">
                                                  No candidate details available.
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {/* Totals Row */}
                        <tr className="bg-gray-300 font-bold">
                          <td colSpan={2} className="border px-4 py-2">Total</td>
                          <td className="border px-4 py-2">{totals.total_resume_forwarded}</td>
                          <td className="border px-4 py-2">{totals.total_pipeline_unique} / {totals.total_pipeline}</td>
                          <td className="border px-4 py-2">{totals.total_turned_up}</td>
                          <td className="border px-4 py-2">{totals.total_selected}</td>
                          <td className="border px-4 py-2">{totals.total_backout}</td>
                          <td className="border px-4 py-2">{totals.total_revenue.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterMonthlyRecords;